import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import { Eye, EyeOff, X } from "lucide-react";
import LeadsPipeline from "@/components/LeadsPipeline";

export default function AdminDashboard({ user }) {
  const [tab, setTab] = useState("overview");
  const [leadsView, setLeadsView] = useState("pipeline");
  const [metrics, setMetrics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [seo, setSeo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSeo, setSavingSeo] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const [m, l, p, s, st] = await Promise.all([
        axios.get(`${API}/admin/metrics`),
        axios.get(`${API}/admin/leads`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/seo`),
        axios.get(`${API}/admin/settings`),
      ]);
      setMetrics(m.data);
      setLeads(l.data);
      setPayments(p.data);
      setSeo(s.data);
      setSettings(st.data);
    } catch (e) {
      toast.error("Failed to load admin data.");
    }
  };

  useEffect(() => { refresh(); }, []);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    navigate("/admin/login", { replace: true });
  };

  const saveSeo = async () => {
    setSavingSeo(true);
    try {
      const payload = { ...seo };
      delete payload.updated_at;
      delete payload.updated_by;
      await axios.put(`${API}/admin/seo`, payload);
      toast.success("SEO settings saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSavingSeo(false);
    }
  };

  const updateSeo = (k, v) => setSeo((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-[#0D1729] text-white">
      {/* Top rail */}
      <div className="border-b border-slate-800/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-6 h-6 border border-white/70 relative">
              <div className="absolute inset-1 bg-white/90" />
            </div>
            <span className="mono-label">OPERATOR CONSOLE · SAUDERTON FORENSICS</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="mono-label text-slate-400 hidden md:inline" data-testid="admin-user-label">
              {user?.email}
            </span>
            <button onClick={logout} className="btn-secondary !py-2 !px-4" data-testid="admin-logout-btn">Sign out</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-8 border-t border-slate-800/70 overflow-x-auto">
          {["overview", "leads", "payments", "seo", "settings"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`py-4 mono-label transition-colors whitespace-nowrap ${tab === t ? "text-white border-b border-white -mb-px" : "text-slate-400 hover:text-white"}`}
              data-testid={`admin-tab-${t}`}
            >
              {t === "settings" ? "API · KEYS" : t}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {tab === "overview" && (
          <section data-testid="admin-overview">
            <div className="mono-label mb-6">§ OVERVIEW</div>
            <h1 className="font-serif-display text-4xl">Case ledger summary.</h1>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800/70 border border-slate-800/70">
              <MetricCell label="LEADS" value={metrics?.leads ?? "—"} />
              <MetricCell label="PAID RETAINERS" value={metrics?.paid_payments ?? "—"} />
              <MetricCell label="REVENUE (USD)" value={metrics ? `$${(metrics.revenue_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"} />
            </div>

            {/* Integration health */}
            {settings && (
              <div className="mt-10">
                <div className="mono-label mb-4">§ INTEGRATION HEALTH</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800/70 border border-slate-800/70">
                  <HealthCell label="STRIPE" ok={settings.stripe_configured} />
                  <HealthCell label="BTC · BLOCKONOMICS" ok={settings.btc_configured} />
                  <HealthCell label="EMAIL · SENDGRID" ok={settings.email_configured} />
                </div>
              </div>
            )}
          </section>
        )}

        {tab === "leads" && (
          <section data-testid="admin-leads">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
              <div>
                <div className="mono-label">§ INTAKE</div>
                <h1 className="font-serif-display text-4xl mt-2">Case feasibility inquiries.</h1>
              </div>
              <div className="flex items-center gap-3">
                <div className="hairline flex" data-testid="leads-view-toggle">
                  <button
                    onClick={() => setLeadsView("pipeline")}
                    className={`mono-label px-4 py-3 transition-colors ${leadsView === "pipeline" ? "bg-white text-[#0D1729]" : "text-slate-400 hover:text-white"}`}
                    data-testid="leads-view-pipeline"
                  >
                    Pipeline
                  </button>
                  <button
                    onClick={() => setLeadsView("table")}
                    className={`mono-label px-4 py-3 transition-colors border-l border-slate-800 ${leadsView === "table" ? "bg-white text-[#0D1729]" : "text-slate-400 hover:text-white"}`}
                    data-testid="leads-view-table"
                  >
                    Table
                  </button>
                </div>
                <button onClick={refresh} className="btn-secondary" data-testid="admin-refresh-leads">Refresh</button>
              </div>
            </div>

            {leadsView === "pipeline" ? (
              <LeadsPipeline leads={leads} onRefresh={refresh} />
            ) : (
            <div className="hairline overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="text-left border-b border-slate-800/70">
                    {["DATE", "NAME", "EMAIL", "FIRM", "CASE TYPE", "STATUS", "PURCHASES", "DETAIL"].map((h) => (
                      <th key={h} className="mono-label p-4">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td className="p-6 text-slate-500 mono-label" colSpan={8}>NO LEADS YET.</td></tr>
                  )}
                  {leads.map((l) => {
                    const purchases = l.purchases || [];
                    const isPaid = ["retained", "in_trace", "delivered"].includes(l.status) || purchases.length > 0;
                    return (
                      <tr key={l.id} className="border-b border-slate-800/40 hover:bg-[#1B2843]">
                        <td className="p-4 font-mono text-xs text-slate-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                        <td className="p-4 text-white">{l.name}</td>
                        <td className="p-4 font-mono text-xs text-slate-300">{l.email}</td>
                        <td className="p-4 text-slate-300">{l.firm || "—"}</td>
                        <td className="p-4 text-slate-300">{l.case_type}</td>
                        <td className="p-4">
                          <span
                            className={`mono-label inline-flex items-center gap-2 ${isPaid ? "text-emerald-400" : "text-amber-400"}`}
                            data-testid={`lead-status-${l.id}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${isPaid ? "bg-emerald-400" : "bg-amber-400"}`} />
                            {(l.status || "filed").replace("_", " ").toUpperCase()}
                          </span>
                        </td>
                        <td className="p-4">
                          {purchases.length === 0 ? (
                            <span className="mono-label text-slate-600">—</span>
                          ) : (
                            <div className="space-y-1">
                              {purchases.map((p) => (
                                <div key={p.transaction_ref || `${p.provider}-${p.paid_at}`} className="flex items-center gap-2 text-xs">
                                  <span className="mono-label text-slate-500">{(p.provider || "").toUpperCase()}</span>
                                  <span className="text-white">{p.service_name || p.service_type}</span>
                                  <span className="font-mono text-emerald-400">${Number(p.amount || 0).toFixed(0)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-slate-400 text-xs max-w-xs truncate" title={l.description}>{l.description}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </section>
        )}

        {tab === "payments" && (
          <section data-testid="admin-payments">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="mono-label">§ RETAINERS</div>
                <h1 className="font-serif-display text-4xl mt-2">Payment ledger.</h1>
              </div>
              <button onClick={refresh} className="btn-secondary" data-testid="admin-refresh-payments">Refresh</button>
            </div>
            <div className="hairline overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-payments-table">
                <thead>
                  <tr className="text-left border-b border-slate-800/70">
                    {["DATE", "PROVIDER", "SERVICE", "AMOUNT", "STATUS", "SESSION"].map((h) => (
                      <th key={h} className="mono-label p-4">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr><td className="p-6 text-slate-500 mono-label" colSpan={6}>NO PAYMENTS YET.</td></tr>
                  )}
                  {payments.map((p) => (
                    <tr key={p.session_id || p.id} className="border-b border-slate-800/40">
                      <td className="p-4 font-mono text-xs text-slate-400 whitespace-nowrap">{new Date(p.created_at).toLocaleString()}</td>
                      <td className="p-4 mono-label">{p.provider?.toUpperCase()}</td>
                      <td className="p-4 text-slate-300">{p.service_type}</td>
                      <td className="p-4 font-mono text-white">${Number(p.amount || 0).toFixed(2)}</td>
                      <td className="p-4 mono-label">{(p.payment_status || "").toUpperCase()}</td>
                      <td className="p-4 font-mono text-xs text-slate-500 max-w-[200px] truncate">{p.session_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "seo" && seo && (
          <section data-testid="admin-seo">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="mono-label">§ SEO METADATA</div>
                <h1 className="font-serif-display text-4xl mt-2">Public metadata controls.</h1>
              </div>
              <a href="/" target="_blank" rel="noreferrer" className="btn-secondary" data-testid="seo-preview-link">Preview Live →</a>
            </div>
            <p className="text-slate-400 mt-2 text-sm max-w-2xl">
              All fields are injected into the public landing page on next load. Use Custom Head HTML
              for verifications / JSON-LD / extra meta tags supplied by your SEO analyst. Use Footer
              Tracking Code for GA4, GTM, Meta Pixel, etc. Script tags execute on insert.
            </p>

            <div className="mt-10 hairline p-8 bg-[#152138] space-y-8 max-w-4xl">
              <SeoField label="Page Title (≤60 chars)" value={seo.page_title} onChange={(v) => updateSeo("page_title", v)} testid="seo-title" />
              <SeoField label="Meta Description (≤160 chars)" value={seo.meta_description} onChange={(v) => updateSeo("meta_description", v)} testid="seo-desc" textarea />
              <SeoField label="H1 Headline" value={seo.h1} onChange={(v) => updateSeo("h1", v)} testid="seo-h1" />
              <SeoField label="H2 Sub-headline" value={seo.h2} onChange={(v) => updateSeo("h2", v)} testid="seo-h2" textarea />
              <SeoField label="SEO Keywords (comma-separated)" value={seo.keywords} onChange={(v) => updateSeo("keywords", v)} testid="seo-keywords" textarea />
              <SeoField label="Canonical URL" value={seo.canonical_url} onChange={(v) => updateSeo("canonical_url", v)} testid="seo-canonical" placeholder="https://johnsauderton.com/" />
              <SeoField label="Robots Directive" value={seo.robots} onChange={(v) => updateSeo("robots", v)} testid="seo-robots" placeholder="index, follow" />

              <div className="border-t border-slate-800/70 pt-8">
                <div className="mono-label mb-2 text-white">OPENGRAPH · SOCIAL CARDS</div>
                <div className="space-y-6 mt-4">
                  <SeoField label="OG Image URL (1200×630 recommended)" value={seo.og_image} onChange={(v) => updateSeo("og_image", v)} testid="seo-ogimg" />
                  <SeoField label="OG Title (defaults to Page Title)" value={seo.og_title} onChange={(v) => updateSeo("og_title", v)} testid="seo-ogtitle" />
                  <SeoField label="OG Description (defaults to Meta Description)" value={seo.og_description} onChange={(v) => updateSeo("og_description", v)} testid="seo-ogdesc" textarea />
                </div>
              </div>

              <div className="border-t border-slate-800/70 pt-8">
                <div className="mono-label mb-2 text-white">CUSTOM HEAD HTML</div>
                <p className="text-slate-400 text-xs mt-1 mb-4">
                  Paste raw HTML here — verification meta tags (Google Search Console, Bing), JSON-LD structured data,
                  hreflang, additional &lt;link&gt; tags, etc. Anything pasted is injected into &lt;head&gt; on every page load.
                </p>
                <CodeArea value={seo.head_html} onChange={(v) => updateSeo("head_html", v)} testid="seo-headhtml" placeholder={'<meta name="google-site-verification" content="..." />\n<script type="application/ld+json">{ ... }</script>'} />
              </div>

              <div className="border-t border-slate-800/70 pt-8">
                <div className="mono-label mb-2 text-white">FOOTER TRACKING CODE</div>
                <p className="text-slate-400 text-xs mt-1 mb-4">
                  Paste GA4 / GTM / Meta Pixel / Hotjar / Clarity tags. Injected before &lt;/body&gt;. Script tags execute.
                </p>
                <CodeArea value={seo.footer_html} onChange={(v) => updateSeo("footer_html", v)} testid="seo-footerhtml" placeholder={'<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXX"></script>\n<script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag("js",new Date());gtag("config","G-XXXX");</script>'} />
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-slate-800/70 sticky bottom-0 bg-[#152138] -mx-8 px-8 py-4">
                <span className="mono-label">CHANGES TAKE EFFECT ON NEXT PAGE LOAD</span>
                <button onClick={saveSeo} disabled={savingSeo} className="btn-primary" data-testid="seo-save-btn">
                  {savingSeo ? "Saving…" : "Commit Changes"}
                </button>
              </div>
            </div>
          </section>
        )}

        {tab === "settings" && settings && (
          <SettingsPanel settings={settings} onSaved={refresh} />
        )}
      </div>
    </div>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className="bg-[#152138] p-10">
      <div className="mono-label">{label}</div>
      <div className="font-serif-display text-5xl text-white mt-6">{value}</div>
    </div>
  );
}

function HealthCell({ label, ok }) {
  return (
    <div className="bg-[#152138] p-8 flex items-center justify-between">
      <div>
        <div className="mono-label">{label}</div>
        <div className={`font-mono mt-3 text-sm ${ok ? "text-emerald-400" : "text-amber-400"}`}>
          {ok ? "● CONFIGURED" : "○ NOT CONFIGURED"}
        </div>
      </div>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-400 node-pulse" : "bg-amber-500"}`} />
    </div>
  );
}

function SeoField({ label, value, onChange, testid, textarea, placeholder }) {
  return (
    <label className="block">
      <span className="mono-label block mb-3">{label}</span>
      {textarea ? (
        <textarea className="input-line" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} />
      ) : (
        <input className="input-line" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} />
      )}
    </label>
  );
}

function CodeArea({ value, onChange, testid, placeholder }) {
  return (
    <textarea
      className="w-full bg-[#0D1729] border border-slate-800 text-white font-mono text-xs p-4 min-h-[180px] focus:border-white outline-none resize-y"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      placeholder={placeholder}
      spellCheck={false}
      autoCorrect="off"
    />
  );
}

function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState({
    stripe_api_key: "",
    blockonomics_api_key: "",
    blockonomics_xpub: "",
    sendgrid_api_key: "",
    resend_api_key: "",
    sender_email: settings.sender_email || "",
    lead_notify_emails: settings.lead_notify_emails || "",
  });
  const [show, setShow] = useState({});
  const [saving, setSaving] = useState(false);
  const [tests, setTests] = useState({});
  const [testing, setTesting] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleShow = (k) => setShow((s) => ({ ...s, [k]: !s[k] }));

  const runTest = async (provider) => {
    setTesting(provider);
    try {
      const { data } = await axios.post(`${API}/admin/settings/test/${provider}`);
      setTests((t) => ({ ...t, [provider]: data }));
      if (data.ok) {
        if (provider === "stripe" && data.mode === "test") {
          toast.warning(`Stripe key OK — but it's a TEST key (sk_test_). Live payments need sk_live_.`);
        } else {
          toast.success(`${provider.toUpperCase()} test passed.`);
        }
      } else {
        toast.error(`${provider.toUpperCase()} test failed: ${data.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(`Test failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setTesting(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Don't send empty secret fields — they would be ignored anyway by backend
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (k === "sender_email" || k === "lead_notify_emails") payload[k] = v;
        else if (v && v.trim()) payload[k] = v.trim();
      });
      await axios.put(`${API}/admin/settings`, payload);
      toast.success("Settings saved. Integrations reload on next request.");
      // Clear secret inputs after save
      setForm((f) => ({
        ...f,
        stripe_api_key: "",
        blockonomics_api_key: "",
        blockonomics_xpub: "",
        sendgrid_api_key: "",
        resend_api_key: "",
      }));
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async (field) => {
    if (!window.confirm(`Clear stored ${field}? The .env fallback (if any) will be used.`)) return;
    try {
      await axios.delete(`${API}/admin/settings/${field}`);
      toast.success(`${field} cleared.`);
      await onSaved();
    } catch (e) {
      toast.error("Failed to clear.");
    }
  };

  const SECRETS = [
    { key: "stripe_api_key", label: "Stripe Secret Key", placeholder: "sk_live_… or sk_test_…", help: "https://dashboard.stripe.com/apikeys", test: "stripe" },
    { key: "blockonomics_api_key", label: "Blockonomics API Key", placeholder: "blockonomics API key", help: "https://www.blockonomics.co/merchants → Settings", test: "blockonomics" },
    { key: "blockonomics_xpub", label: "Bitcoin xPub", placeholder: "xpub6C…", help: "Export from your BTC wallet (Sparrow / Exodus / Blockchain.com)", test: "blockonomics" },
    { key: "sendgrid_api_key", label: "SendGrid API Key", placeholder: "SG.xxx…", help: "https://app.sendgrid.com → Settings → API Keys (Full Access)", test: "sendgrid" },
    { key: "resend_api_key", label: "Resend API Key (optional fallback)", placeholder: "re_…", help: "https://resend.com → API Keys" },
  ];

  return (
    <section data-testid="admin-settings">
      <div className="mono-label mb-2">§ API · KEYS</div>
      <h1 className="font-serif-display text-4xl">Integration credentials.</h1>
      <p className="text-slate-400 mt-4 text-sm max-w-2xl">
        Keys saved here are stored encrypted-at-rest in the database and override any
        environment defaults. Existing values are masked — leave blank to keep the
        current value, or paste a new value to replace it.
      </p>

      <div className="mt-10 hairline p-8 bg-[#152138] space-y-8 max-w-3xl">
        {SECRETS.map(({ key, label, placeholder, help, test }) => {
          const status = settings[key];
          const result = test ? tests[test] : null;
          return (
            <div key={key} className="border-b border-slate-800/60 pb-6 last:border-b-0 last:pb-0">
              <div className="flex items-center justify-between mb-3">
                <span className="mono-label">{label}</span>
                <span className={`mono-label ${status?.set ? "text-emerald-400" : "text-amber-400"}`}>
                  {status?.set ? `● SET · ${status.preview}` : "○ NOT SET"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type={show[key] ? "text" : "password"}
                  className="input-line flex-1"
                  value={form[key]}
                  onChange={(e) => update(key, e.target.value)}
                  placeholder={placeholder}
                  data-testid={`settings-${key.replace(/_/g, "-")}`}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={() => toggleShow(key)}
                  className="btn-secondary !py-3 !px-3"
                  title={show[key] ? "Hide" : "Show"}
                  data-testid={`settings-toggle-${key.replace(/_/g, "-")}`}
                >
                  {show[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {test && status?.set && (
                  <button
                    type="button"
                    onClick={() => runTest(test)}
                    disabled={testing === test}
                    className="btn-secondary !py-3 !px-4 whitespace-nowrap"
                    title={`Test live ${test} API`}
                    data-testid={`settings-test-${key.replace(/_/g, "-")}`}
                  >
                    {testing === test ? "TESTING…" : "TEST"}
                  </button>
                )}
                {status?.set && (
                  <button
                    type="button"
                    onClick={() => clearKey(key)}
                    className="btn-secondary !py-3 !px-3"
                    title="Clear stored value"
                    data-testid={`settings-clear-${key.replace(/_/g, "-")}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {result && (
                <div
                  className={`mt-3 px-3 py-2 text-xs font-mono border ${result.ok ? "border-emerald-700 bg-emerald-950/30 text-emerald-300" : "border-red-700 bg-red-950/30 text-red-300"}`}
                  data-testid={`settings-test-result-${test}`}
                >
                  {result.ok ? (
                    <>
                      <div>● TEST PASSED {result.mode ? `· MODE: ${String(result.mode).toUpperCase()}` : ""}</div>
                      {result.warning && <div className="mt-1 text-amber-300">⚠ {result.warning}</div>}
                      {test === "stripe" && (
                        <div className="mt-1">{result.livemode ? "LIVE PAYMENTS ENABLED" : "TEST/SANDBOX MODE — switch to sk_live_ for real payments"}</div>
                      )}
                      {test === "blockonomics" && result.xpub_preview && (
                        <div className="mt-1">XPUB · {result.xpub_preview}</div>
                      )}
                      {test === "sendgrid" && (
                        <div className="mt-1">SCOPES: {result.scopes_count} · CAN SEND MAIL: {result.can_send_mail ? "YES" : "NO"}</div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>✗ TEST FAILED {result.status ? `· HTTP ${result.status}` : ""}</div>
                      <div className="mt-1 text-red-200">{result.error}</div>
                    </>
                  )}
                </div>
              )}
              <a href={help} target="_blank" rel="noreferrer" className="mono-label text-slate-500 hover:text-white mt-2 inline-block">
                ↗ {help}
              </a>
            </div>
          );
        })}

        <div className="border-t border-slate-800/70 pt-8">
          <div className="mono-label mb-2 text-white">EMAIL · SENDER + RECIPIENTS</div>
          <div className="space-y-6 mt-4">
            <label className="block">
              <span className="mono-label block mb-3">Sender Email (must be verified in SendGrid)</span>
              <input
                className="input-line"
                value={form.sender_email}
                onChange={(e) => update("sender_email", e.target.value)}
                placeholder="no-reply@johnsauderton.com"
                data-testid="settings-sender-email"
              />
            </label>
            <label className="block">
              <span className="mono-label block mb-3">Lead Notification Emails (comma-separated)</span>
              <input
                className="input-line"
                value={form.lead_notify_emails}
                onChange={(e) => update("lead_notify_emails", e.target.value)}
                placeholder="John@mythiant.com, blackveilone@gmail.com"
                data-testid="settings-notify-emails"
              />
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-slate-800/70">
          <span className="mono-label">SECRETS NEVER LEAVE THE SERVER</span>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="settings-save-btn">
            {saving ? "Saving…" : "Commit Settings"}
          </button>
        </div>
      </div>
    </section>
  );
}
